// ConvertPendingModal — book in a pending enquiry as a confirmed booking.
//
// IMPORTANT — semantic deviation from the Wave B3 brief:
//   The brief described pending → receipt conversion. The actual server
//   `POST /api/v1/collections/pending/:enc_id/convert` promotes a pending row into
//   a confirmed BOOKING (UI_AUDIT §2.7 calls this "Book In"). Following
//   the server + UI_AUDIT.
//
// Server contract (server/src/modules/scrap/pending/pending.types.ts
// `convertPendingSchema`):
//   { date, timeframe, driver?, source?, payment_mode?, send_email,
//     send_sms, reminder_email, reminder_sms }
// The pending row's customer + vehicle fields carry over verbatim so the
// modal only collects the schedule/notification fields.

import { useEffect, useState } from 'react';
import axios from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '../../../components/ui/Modal/Modal';
import { Button } from '../../../components/ui/Button/Button';
import { Select } from '../../../components/form/Select';
import { DatePicker } from '../../../components/form/DatePicker';
import { Checkbox } from '../../../components/form/Checkbox';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import { useToast } from '../../../hooks/useToast';
import { useActiveDrivers } from '../../bookings/hooks/useDriversList';
import {
  useWorkSources,
  usePaymentModes,
  useTimeSlots,
} from '../../bookings/hooks/useReferenceData';
import type {
  ConvertPendingPayload,
  ConvertPendingResponse,
  PendingSummary,
  SlotDayType,
  Timeframe,
} from '../../../types/api';
import styles from './ConvertPendingModal.module.css';

function slotDayType(dateStr: string): SlotDayType {
  if (!dateStr) return 'weekday';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return 'weekday';
  const day = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])).getDay();
  return day === 0 || day === 6 ? 'weekend' : 'weekday';
}

export interface ConvertPendingModalProps {
  open: boolean;
  pending: PendingSummary | null;
  onClose: () => void;
  /** Optional success callback — receives the new booking summary so
   *  the page can navigate or update local state. */
  onConverted?: (response: ConvertPendingResponse) => void;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function ConvertPendingModal({
  open,
  pending,
  onClose,
  onConverted,
}: ConvertPendingModalProps): JSX.Element | null {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [date, setDate] = useState(todayIso());
  const [timeframe, setTimeframe] = useState<Timeframe>('AM');

  const drivers = useActiveDrivers();
  const sources = useWorkSources();
  const paymentModes = usePaymentModes();
  const timeSlots = useTimeSlots(slotDayType(date));
  const [driver, setDriver] = useState<number | null>(null);
  const [source, setSource] = useState<number | null>(null);
  const [paymentMode, setPaymentMode] = useState<number | null>(null);
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(true);

  useEffect(() => {
    if (!open || !pending) return;
    setDate(todayIso());
    setTimeframe('AM');
    setDriver(null);
    setSource(pending.source_id);
    setPaymentMode(null);
    setSendEmail(true);
    setSendSms(true);
  }, [open, pending]);

  const mutation = useMutation<ConvertPendingResponse, unknown, ConvertPendingPayload>({
    mutationFn: async (payload) => {
      if (!pending) throw new Error('No pending booking selected');
      const res = await apiClient.post<ConvertPendingResponse>(
        endpoints.pending.convert(pending.enc_id),
        payload,
      );
      if (!res.data) throw new Error('Convert response missing data');
      return res.data;
    },
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.pending.all });
      await queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all });
      await queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      // Invalidate all diary sub-keys so converted bookings appear immediately
      // in the diary view (diary.today, diary.week, diary.drivers, etc.).
      await queryClient.invalidateQueries({ queryKey: ['diary'] });
      toast.success(`Booked in as ${response.booking.reference}`);
      onConverted?.(response);
      onClose();
    },
    onError: (err) => {
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ??
          'Could not convert pending booking.'
        : 'Could not convert pending booking.';
      toast.error(message);
    },
  });

  if (!pending) return null;

  const driverOptions = (drivers.data ?? []).map((d) => ({
    value: String(d.id),
    label: d.name,
  }));
  const sourceOptions = (sources.data ?? []).map((s) => ({
    value: String(s.id),
    label: s.name,
  }));
  const paymentOptions = (paymentModes.data ?? []).map((p) => ({
    value: String(p.id),
    label: p.name,
  }));

  function handleConfirm() {
    mutation.mutate({
      date,
      timeframe,
      driver,
      source,
      payment_mode: paymentMode,
      send_email: sendEmail,
      send_sms: sendSms,
      reminder_email: true,
      reminder_sms: false,
    });
  }

  // We don't pre-render success state inside the modal — onConverted
  // closes the modal and the parent toasts. The brief mentioned a "View
  // Receipt" option — that doesn't apply here (this flow produces a
  // booking, not a receipt — the brief's pending→receipt assumption
  // does not match the server schema, see HANDOFF Wave B3 deviations).

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? () => undefined : onClose}
      title={`Book In — ${pending.car_registration}`}
      size="md"
      preventClose={mutation.isPending}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Keep in queue
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            loading={mutation.isPending}
          >
            Book in
          </Button>
        </>
      }
    >
      <div className={styles.summary}>
        <h4>Pending booking</h4>
        <dl>
          <div>
            <dt>Customer</dt>
            <dd>{pending.name} · {pending.phone}</dd>
          </div>
          <div>
            <dt>Vehicle</dt>
            <dd>{pending.car_make_model} · {pending.car_registration}</dd>
          </div>
          <div>
            <dt>Address</dt>
            <dd>{pending.address}, {pending.postcode}</dd>
          </div>
          <div>
            <dt>Indicative price</dt>
            <dd>{pending.price !== null ? `£${pending.price.toFixed(2)}` : '—'}</dd>
          </div>
        </dl>
      </div>

      <div className={styles.body}>
        <DatePicker
          label="Collection date"
          required
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <Select
          label="Time slot"
          required
          options={(timeSlots.data ?? []).map((t) => ({ value: t.label, label: t.label }))}
          value={timeframe}
          onChange={(e) => setTimeframe(e.target.value as Timeframe)}
        />
        <Select
          label="Driver"
          options={driverOptions}
          placeholder={drivers.isLoading ? 'Loading…' : '—'}
          value={driver !== null ? String(driver) : ''}
          onChange={(e) =>
            setDriver(e.target.value === '' ? null : Number(e.target.value))
          }
        />
        <Select
          label="Work source"
          options={sourceOptions}
          placeholder={
            sources.isLoading
              ? 'Loading…'
              : sourceOptions.length === 0
                ? 'No options — leave blank'
                : '—'
          }
          value={source !== null ? String(source) : ''}
          onChange={(e) =>
            setSource(e.target.value === '' ? null : Number(e.target.value))
          }
        />
        <Select
          label="Payment mode"
          options={paymentOptions}
          placeholder={
            paymentModes.isLoading
              ? 'Loading…'
              : paymentOptions.length === 0
                ? 'No options — leave blank'
                : '—'
          }
          value={paymentMode !== null ? String(paymentMode) : ''}
          onChange={(e) =>
            setPaymentMode(e.target.value === '' ? null : Number(e.target.value))
          }
        />

        <div className={styles.notify}>
          <Checkbox
            checked={sendEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
            label="Send confirmation email"
          />
          <Checkbox
            checked={sendSms}
            onChange={(e) => setSendSms(e.target.checked)}
            label="Send confirmation SMS"
          />
        </div>

      </div>
    </Modal>
  );
}
